"use client";

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

export default function ResearchLabPage() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section
        style={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '0 30px' : '0 40px',
        }}
      >
        <motion.div
          style={{ maxWidth: '1200px', width: '100%', textAlign: 'left' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <h1 style={{
            fontSize: isMobile ? '3rem' : '5.5rem',
            fontWeight: 400,
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
            color: '#583b1f',
          }}>
            We leverage{' '}
            <span style={{
              backgroundColor: 'rgba(255, 182, 193, 0.6)',
              padding: '0 0.2em',
              borderRadius: '4px',
            }}>
              human-centered research
            </span>
            {' '}to solve the most pressing challenges our world is facing
          </h1>
        </motion.div>
      </section>

      {/* Human-Centered Design Research Lab Section */}
      <section
        style={{
          padding: isMobile ? '60px 30px' : '100px 40px',
          background: '#eae6e1',
        }}
      >
        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <h2 style={{
            fontSize: isMobile ? '2rem' : '2.5rem',
            fontWeight: 400,
            letterSpacing: '-0.03em',
            lineHeight: 1.2,
            color: '#583b1f',
            marginBottom: '0.75rem',
          }}>
            Human-Centered Design Research Lab
          </h2>
          <p style={{
            fontSize: isMobile ? '1.125rem' : '1.4rem',
            fontWeight: 500,
            color: '#583b1f',
            marginBottom: isMobile ? '2rem' : '3rem',
            letterSpacing: '-0.01em',
            lineHeight: 1.8,
            maxWidth: '900px',
          }}>
            We believe design is the most powerful tool we have to better understand one another, interject where there is need, and solve the most pressing challenges our world is facing. Learners and experts in our lab are researching how might we improve the human experience.
          </p>

          <div style={{
            display: 'flex',
            gap: '0',
            flexDirection: 'column',
          }}>
            {[
              { name: 'Design x AI', isUpcoming: true, bgColor: 'rgba(250, 245, 255, 0.3)' }, // Light purple
              { name: 'Patriarchy', count: '87', bgColor: 'rgba(255, 237, 213, 0.3)' }, // Soft peach
              { name: 'Loneliness and isolation', count: '124', bgColor: 'rgba(230, 245, 240, 0.3)' }, // Soft mint
              { name: 'Future of work and education', count: '156', bgColor: 'rgba(245, 235, 255, 0.3)' }, // Soft lavender
              { name: 'Human-AI collaboration', count: '92', bgColor: 'rgba(255, 245, 230, 0.3)' }, // Soft cream
            ].map((challenge, index, array) => (
              <div
                key={index}
                style={{
                  padding: '2rem',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: challenge.bgColor,
                  marginBottom: index < array.length - 1 ? '0.5rem' : '0',
                  borderRadius: '12px',
                }}
              >
                <h3 style={{
                  fontSize: '1.8rem',
                  fontWeight: 400,
                  letterSpacing: '-0.02em',
                  color: '#583b1f',
                  margin: 0,
                }}>
                  {challenge.name}
                </h3>

                {challenge.isUpcoming ? (
                  <span style={{
                    padding: '0.6rem 1rem 0.6rem 0.8rem',
                    backgroundColor: 'white',
                    color: '#000',
                    borderRadius: '24px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}>
                    <span style={{
                      width: '8px',
                      height: '8px',
                      backgroundColor: '#4ade80',
                      borderRadius: '50%',
                      display: 'inline-block',
                    }}></span>
                    Upcoming design program
                  </span>
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                  }}>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#583b1f"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ opacity: 0.6 }}
                    >
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                      <circle cx="9" cy="7" r="4"></circle>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                    </svg>
                    <span style={{
                      fontSize: '1rem',
                      fontWeight: 500,
                      color: '#583b1f',
                      opacity: 0.7,
                    }}>
                      {challenge.count} students globally
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner with us Section */}
      <section style={{
        padding: isMobile ? '60px 20px' : '100px 40px',
        background: 'rgba(229, 224, 218, 0.3)',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: isMobile ? '2rem' : '2.5rem',
            fontWeight: 400,
            letterSpacing: '-0.03em',
            lineHeight: 1.2,
            color: '#583b1f',
            marginBottom: '2rem',
          }}>
            Partner with us → <a href="mailto:hello@dfsgdesign.com" style={{
              color: '#ff8d8d',
              textDecoration: 'none',
              transition: 'opacity 0.3s ease',
            }}>hello@dfsgdesign.com</a>
          </h2>

          <p style={{
            fontSize: isMobile ? '1.125rem' : '1.4rem',
            fontWeight: 500,
            letterSpacing: '-0.01em',
            lineHeight: 1.8,
            color: '#583b1f',
            maxWidth: '900px',
            marginBottom: isMobile ? '2rem' : '3rem',
            textAlign: 'left',
          }}>
            We partner with universities, research institutions, and government agencies to explore how human-centered design and creative research methodologies can address complex societal challenges. Let&apos;s collaborate on groundbreaking research that puts people first.
          </p>

          {/* Partner Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: isMobile ? '1.5rem' : '2rem',
          }}>
            {/* Higher Education Card */}
            <div
              style={{
                background: 'linear-gradient(135deg, #f6f5f4 0%, #f2f0ed 100%)',
                borderRadius: '20px',
                padding: '2.5rem 2rem',
                minHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(212, 165, 116, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(88, 59, 31, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(245, 235, 255, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#583b1f" strokeWidth="2">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                    <path d="M6 12v5c3 3 9 3 12 0v-5" />
                  </svg>
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 400,
                  color: '#583b1f',
                  marginBottom: '1rem',
                  letterSpacing: '-0.02em',
                }}>
                  Higher Education & Research Institutions
                </h3>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'rgba(88, 59, 31, 0.75)',
                  lineHeight: 1.6,
                  marginBottom: '2rem',
                  fontWeight: 500,
                }}>
                  Collaborate with us on research initiatives that explore the intersection of design thinking, human behavior, and emerging technologies to create meaningful impact.
                </p>
              </div>
            </div>

            {/* Government Agencies Card */}
            <div
              style={{
                background: 'linear-gradient(135deg, #f6f5f4 0%, #f2f0ed 100%)',
                borderRadius: '20px',
                padding: '2.5rem 2rem',
                minHeight: '320px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                overflow: 'hidden',
                border: '1px solid rgba(212, 165, 116, 0.1)',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 20px 40px rgba(88, 59, 31, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(230, 245, 240, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#583b1f" strokeWidth="2">
                    <path d="M3 21h18" />
                    <path d="M5 21V7l8-4v18" />
                    <path d="M19 21V11l-6-4" />
                    <rect x="9" y="9" width="4" height="4" />
                    <rect x="9" y="14" width="4" height="4" />
                  </svg>
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: 400,
                  color: '#583b1f',
                  marginBottom: '1rem',
                  letterSpacing: '-0.02em',
                }}>
                  Government & Public Services
                </h3>
                <p style={{
                  fontSize: '1.125rem',
                  color: 'rgba(88, 59, 31, 0.75)',
                  lineHeight: 1.6,
                  marginBottom: '2rem',
                  fontWeight: 500,
                }}>
                  Partner with us to apply human-centered research methods to civic challenges, improving public services and policy outcomes through evidence-based design.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
